import { type Component, createSignal } from 'solid-js';
import { gameStore } from '@/stores/gameStore';
import { useTranslation } from '@/i18n';
import LoadingSpinner from './LoadingSpinner';

interface PhoneVerificationModalProps {
  onSuccess?: () => void;
}

const PhoneVerificationModal: Component<PhoneVerificationModalProps> = (props) => {
  const { t } = useTranslation();
  const [phoneNumber, setPhoneNumber] = createSignal('');
  const [countryCode] = createSignal('+48');
  const [error, setError] = createSignal<string | null>(null);
  const [isSubmitting, setIsSubmitting] = createSignal(false);

  // Format phone number for display (add spaces)
  const formatPhoneDisplay = (value: string): string => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    // Format as XXX XXX XXX
    let formatted = '';
    for (let i = 0; i < digits.length && i < 9; i++) {
      if (i > 0 && i % 3 === 0) {
        formatted += ' ';
      }
      formatted += digits[i];
    }
    return formatted;
  };

  const handlePhoneChange = (e: Event) => {
    const target = e.target as HTMLInputElement;
    const value = target.value;
    // Remove non-digit characters and limit to 9 digits
    const digits = value.replace(/\D/g, '').slice(0, 9);
    setPhoneNumber(formatPhoneDisplay(digits));
    setError(null);
  };

  const handleSubmit = async (e: Event) => {
    e.preventDefault();

    // Validate
    const digits = phoneNumber().replace(/\D/g, '');
    if (digits.length < 9) {
      setError(t('phoneVerification.errorInvalidFormat'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Combine country code with phone number
      const fullNumber = `${countryCode()}${digits}`;
      const result = await gameStore.verifyPhone(fullNumber);

      if (result.success) {
        props.onSuccess?.();
      } else {
        setError(result.error || t('phoneVerification.errorInvalidFormat'));
      }
    } catch (err: any) {
      setError(err.message || t('errors.unknownError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div class="w-full max-w-md">
        <div class="card animate-fade-in">
          <div class="card-header text-center">
            <div class="text-5xl mb-4">
              <span role="img" aria-label="phone">📱</span>
            </div>
            <h2 class="text-2xl font-bold text-white mb-2">
              {t('phoneVerification.title')}
            </h2>
            <p class="text-primary-200">
              {t('phoneVerification.subtitle')}
            </p>
          </div>

          <div class="card-body">
            <form onSubmit={handleSubmit} noValidate>
              <div class="form-group">
                <label for="phone" class="form-label">
                  {t('phoneVerification.phoneNumber')}
                </label>

                <div class="flex gap-2">
                  {/* Country Code Selector (fixed for now) */}
                  <div class="w-24 flex-shrink-0">
                    <div class="form-input bg-primary-700/50 text-center font-medium text-white cursor-default">
                      {countryCode()}
                    </div>
                  </div>

                  {/* Phone Number Input */}
                  <div class="flex-1">
                    <input
                      type="tel"
                      id="phone"
                      class={`form-input ${error() ? 'error' : ''}`}
                      placeholder={t('phoneVerification.phonePlaceholder')}
                      value={phoneNumber()}
                      onInput={handlePhoneChange}
                      disabled={isSubmitting()}
                      autocomplete="tel"
                      inputMode="numeric"
                      pattern="[0-9\s]*"
                    />
                  </div>
                </div>

                <div class="text-sm text-primary-300 mt-2">
                  {t('phoneVerification.phoneHint')}
                </div>

                {error() && (
                  <div class="form-error mt-2">
                    {error()}
                  </div>
                )}
              </div>

              <button
                type="submit"
                class="btn btn-primary btn-full btn-lg mt-6"
                disabled={isSubmitting() || phoneNumber().replace(/\D/g, '').length < 9}
              >
                {isSubmitting() ? (
                  <>
                    <LoadingSpinner size="sm" />
                    {t('phoneVerification.submitting')}
                  </>
                ) : (
                  <>
                    <span role="img" aria-hidden="true">✓</span>
                    {t('phoneVerification.submit')}
                  </>
                )}
              </button>
            </form>
          </div>

          <div class="card-footer">
            <div class="text-center text-primary-300 text-sm">
              <p>
                <span role="img" aria-label="info">ℹ️</span>
                {' '}Twój numer będzie używany wyłącznie do komunikacji w trakcie gry.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhoneVerificationModal;
